import {
  NodeConnection,
  NodePath,
  NodeVisualConnection,
} from '../../../types/node';
import type { Relation, RelationMap } from '../../../types/relation';

type RelationDescendantKey = 'children' | 'connections';

function findPathWithConcreteEndNode(
  [startNode, endNode]: [string | null, string],
  relations: RelationMap,
): Relation['id'][] {
  const path = [endNode];

  while (path[0] !== startNode) {
    const lastNode = path[0];
    const { parentId = null } = relations[lastNode] ?? {};

    if (parentId === null) {
      break;
    }

    path.unshift(parentId);
  }

  return path;
}

function getDescendantsAsIds(
  id: Relation['id'],
  relations: RelationMap,
  key: RelationDescendantKey,
): (NodeConnection['id'] | NodeVisualConnection['id'])[] {
  const { [id]: relation } = relations;

  switch (key) {
    case 'connections':
      return relation.connections.map(({ id, connection }) => connection || id);
    case 'children':
      return relation.children;
  }
}

function getDescendantsForId(
  id: Relation['id'],
  relations: RelationMap,
  key: RelationDescendantKey,
) {
  if (!id || !relations[id]) {
    return [];
  }

  const descendants = getDescendantsAsIds(id, relations, key);

  return descendants.reduce<Relation['id'][]>((acc, id) => {
    acc.push(id, ...getDescendantsForId(id, relations, key));

    return acc;
  }, []);
}

function findAllDescendants(
  startNode: string | null,
  relations: RelationMap,
  key: RelationDescendantKey,
) {
  return startNode
    ? [startNode, ...getDescendantsForId(startNode, relations, key)]
    : [];
}

export function getNodesInPath(
  [nodeA, nodeB]: NodePath,
  relations: RelationMap,
  key: RelationDescendantKey = 'children',
): Relation['id'][] {
  const path = nodeB
    ? findPathWithConcreteEndNode([nodeA, nodeB], relations)
    : findAllDescendants(nodeA, relations, key);

  return path;
}
